<script>
import {DateTime} from "luxon";
import {dateHelper} from "@/assets/plugins/mps-common/time/date-helper";

/**
 * infinityScrollTable 에서 활용하는 헤더 입니다.
 * header 기본 구조
 {
    pk: item을 구분할 수 있는 pk인 컬럼
      ex) true,
    text: 컬럼 명
      ex) 임대업체 코드,
    key: 컬럼 키 (자식 컬럼이 있을 경우 생략 가능)
      ex) rentalcompanyId,
    child: 자식 컬럼 (depth 무제한 이지만 3이하 권장
      ex) [{text: '자식1', key: child01},{text: '자식2', key: child02}]
    width: 가로 크기
      ex) '60px',
    sortable: 정렬 가능여부
      ex) true,
    align: 컬럼 아이템 정렬 기준
      ex) 'right',
    read: 컬럼 아이템 표시 방식
      ex) (item, v) => v + item.rentalcompanyName
    input: { 컬럼 아이템 직접 수정 시 설정 값
      type: 수정 시 사용할 타입
        ex) 'number', 'button', 'datepicker' ... 'text', 'textarea'
      trueValue: checkbox, button 등에 사용할 true-value
        ex) 'Y',
      trueColor: true-value 인경우 색상
        ex) '#01579B',
      falseValue: checkbox, button 등에 사용할 false-value
        ex) 'N',
      color: default-color이면서 false 컬러
        ex) '#263238',
      disable: 컬럼 아이템 수정 불가 조건
        ex) (item) => item.useYn === 'N'
      callBack: item 수정 후 처리 콜백
        ex) (item) => this.onUpdateRentalCompanyInfo(item),
    },
  },
 * */
export default {
  name: 'Headers',
  data() {
    return {
      mpsUsageHeaders: [
        {
          text: '관리코드',
          key: 'salesCustomCode',
          align: 'center',
          width: '100px',
          sortable: true,
        },
        {
          text: '임대업체 코드',
          key: 'rentalcompanyId',
          align: 'center',
          width: '100px',
          sortable: true,
        },
        {
          text: '정산월',
          key: 'salesMonth',
          align: 'center',
          width: '100px',
          sortable: true,
          read: (item, v) => {
            if (!v)
              return '정산 없음'
            try {
              const parseDate = this.$parseDate(v)
              return parseDate.toFormat('yyyy-MM')
            }catch (e) {
              return '변환 오류'
            }
          }
        },
        {
          text: '임대업체 명',
          key: 'rentalcompanyName',
          align: 'left',
          width: '100px',
          sortable: true,
        },
        {
          text: '사용기간',
          child: [
            {
              text: '시작일',
              key: 'startDate',
              align: 'center',
              width: '100px',
              sortable: false,
            },
            {
              text: '종료일',
              key: 'endDate',
              align: 'center',
              width: '100px',
              sortable: false,
            },
          ],
        },
        {
          text: '기본료',
          key: 'baseFee',
          align: 'right',
          width: '100px',
          sortable: true,
          read: (item, v) => {
            return v ? Number(v).toLocaleString() + '원' : '없음'
          },
        },
        {
          text: '추가료',
          key: 'sumAddFee',
          align: 'right',
          width: '100px',
          sortable: true,
          read: (item, v) => {
            return v ? Number(v).toLocaleString() + '원' : '없음'
          },
        },
        {
          text: '총 금액',
          key: 'totalFee',
          align: 'right',
          width: '100px',
          sortable: true,
          read: (item, v) => {
            return v ? Number(v).toLocaleString() + '원' : '없음'
          },
        },
        {
          text: '전월(기본료+추가료=총)',
          key: 'prevTotalFee',
          align: 'right',
          width: '170px',
          sortable: false,
          read: (item, v) => {
            return v ? `${item.prevBaseFee ? Number(item.prevBaseFee).toLocaleString() : 0} + ${item.prevSumAddFee ? Number(item.prevSumAddFee).toLocaleString() : 0} = ${Number(v).toLocaleString()}원` : '없음'
          },
        },
        {
          text: '전월 대비',
          key: 'totalFee',
          align: 'right',
          width: '100px',
          sortable: false,
          read: (item, v) => {
            if (!item.prevTotalFee) {
              return '전월 없음'
            }
            const result = (v ? Number(v): 0) - (item.prevTotalFee ? Number(item.prevTotalFee): 0)
            return (result > 0 ? '+' : '') + result.toLocaleString() + '원'
          },
        },
        {
          text: '최종여부',
          key: 'useYn',
          align: 'center',
          width: '80px',
          sortable: false,
        },
        {
          text: '등록자',
          key: 'registerName',
          align: 'center',
          width: '100px',
          sortable: false,
        },
        {
          text: '등록일시',
          key: 'regDatetime',
          align: 'center',
          width: '100px',
          sortable: true,
        },
      ],

      basePlanHeaders: [
        {
          text: '요금제 명',
          key: 'planName',
          align: 'center',
          width: '100px',
          sortable: false,
        },
        {
          text: '기본료',
          key: 'baseFee',
          align: 'right',
          width: '60px',
          sortable: false,
          read: (item) => item.baseFee ? Number(item.baseFee).toLocaleString() + '원' : '0원'
        },
        {
          text: '사용',
          key: 'useYn',
          align: 'center',
          width: '30px',
          sortable: false,
          read: (item) => item.useYn ? item.useYn : '-'
        },
      ],

      // 회원 관리 > 임대업체 관리 화면 Header
      membershipHeaders: [
        {
          text: '관리코드',
          key: 'salesCustomCode',
          width: '110px',
          sortable: true,
          input: {
            type: 'text',
            callBack: (item) => this.onUpdateRentalCompanyInfo(item),
          },
        },
        {
          pk: true,
          text: '업체코드',
          key: 'rentalcompanyId',
          width: '70px',
          sortable: true,
        },
        {
          text: '업체명',
          key: 'rentalcompanyName',
          width: '150px',
          align: 'left',
          sortable: true,
        },
        {
          text: '사업자번호',
          key: 'businessNo',
          width: '100px',
          sortable: true,
        },
        {
          text: '그룹 목록',
          key: 'groupNamesText',
          width: '100px',
          read: (item) => item.groupNamesText ? item.groupNames[0] + (item.groupNames.length > 1 ? '...' : '') : '-'
        },
        {
          text: '100160',
          key: 'attr01',
          width: '80px',
          sortable: true,
          input: {
            type: 'text',
            callBack: (item) => this.onUpdateRentalCompanyInfo(item),
          },
        },
        {
          text: '약정여부',
          key: 'contractYn',
          align: '',
          width: '30px',
          sortable: true,
        },
        {
          text: '요금제',
          key: 'planName',
          align: '',
          width: '120px',
          sortable: false,
        },
        {
          text: '설정 정보',
          child: [
            {
              text: '승인',
              key: 'approvedYn',
              align: 'center',
              width: '40px',
              sortable: true,
              input: {
                type: 'button',
                trueValue: 'Y',
                trueColor: '#6f6f6f',
                falseValue: 'N',
                color: '#01579B',
                callBack: (item) => this.onApproveRentalCompany(item),
              },
              read: (item) => item.approvedYn === 'Y' ? '완료' : '승인'
            },
            {
              text: '사용여부',
              key: 'useYn',
              align: 'center',
              width: '40px',
              input: {
                type: 'button',
                trueValue: 'Y',
                trueColor: '#01579B',
                falseValue: 'N',
                color: '#263238',
                callBack: (item) => this.onUpdateRentalCompanyConfig('useYn', item),
              },
            },
            {
              text: '실사용 여부',
              key: 'actualYn',
              width: '40px',
              sortable: true,
              input: {
                type: 'button',
                trueValue: 'Y',
                trueColor: '#01579B',
                falseValue: 'N',
                color: '#263238',
                callBack: (item) => this.onRentalcompanyActualClick(item),
              },
            },
            {
              text: '월사용량',
              key: 'useAutoUsage',
              align: 'center',
              width: '40px',
              input: {
                type: 'button',
                trueValue: 'Y',
                trueColor: '#01579B',
                falseValue: 'N',
                color: '#263238',
                callBack: (item) => this.onUpdateRentalCompanyConfig('useAutoUsage', item),
              },
            },
            {
              text: '매출',
              key: 'useAutoSales',
              align: 'center',
              width: '40px',
              input: {
                type: 'button',
                trueValue: 'Y',
                trueColor: '#01579B',
                falseValue: 'N',
                color: '#263238',
                callBack: (item) => this.onUpdateRentalCompanyConfig('useAutoSales', item),
              },
            },
            {
              text: 'CMS',
              key: 'useAutoCms',
              align: 'center',
              width: '40px',
              input: {
                type: 'button',
                trueValue: 'Y',
                trueColor: '#01579B',
                falseValue: 'N',
                color: '#263238',
                callBack: (item) => this.onUpdateRentalCompanyConfig('useAutoCms', item),
              },
            },
            {
              text: 'TAX',
              key: 'useAutoTax',
              align: 'center',
              width: '40px',
              input: {
                type: 'button',
                trueValue: 'Y',
                trueColor: '#01579B',
                falseValue: 'N',
                color: '#263238',
                callBack: (item) => this.onUpdateRentalCompanyConfig('useAutoTax', item),
              },
            },
            {
              text: '거래명세서',
              key: 'useAutoStatement',
              align: 'center',
              width: '40px',
              input: {
                type: 'button',
                trueValue: 'Y',
                trueColor: '#01579B',
                falseValue: 'N',
                color: '#263238',
                callBack: (item) => this.onUpdateRentalCompanyConfig('useAutoStatement', item),
              },
            },
            {
              text: '접속 허용',
              key: 'loginApproveYn',
              align: 'center',
              width: '40px',
              input: {
                type: 'button',
                trueValue: 'Y',
                trueColor: '#01579B',
                falseValue: 'N',
                color: '#263238',
                callBack: (item) => this.onUpdateRentalCompanyConfig('loginApproveYn', item),
              },

            },
            {
              text: '업무 대행',
              key: 'agencyUseYn',
              align: 'center',
              width: '40px',
              input: {
                type: 'button',
                trueValue: 'Y',
                trueColor: '#01579B',
                falseValue: 'N',
                color: '#263238',
                callBack: (item) => this.onUpdateRentalCompanyConfig('agencyUseYn', item),
              },

            },
            {
              text: '알림톡',
              key: 'kakaoTalkYn',
              align: 'center',
              width: '40px',
              input: {
                type: 'button',
                trueValue: 'Y',
                trueColor: '#01579B',
                falseValue: 'N',
                color: '#263238',
                callBack: (item) => this.onKakaoTalkYnClick(item),
              },

            },
            {
              text: 'NTS',
              key: 'ntsTaxYn',
              align: 'center',
              width: '40px',
              input: {
                type: 'button',
                trueValue: 'Y',
                trueColor: '#01579B',
                falseValue: 'N',
                color: '#263238',
                callBack: (item) => this.onNtsTaxYnClick(item),
              },

            },
            {
              text: '거래내역조회',
              key: 'barobillYn',
              align: 'center',
              width: '40px',
              input: {
                type: 'button',
                trueValue: 'Y',
                trueColor: '#01579B',
                falseValue: 'N',
                color: '#263238',
                callBack: (item) => this.onBarobillYnClick(item),
              },

            },
            {
              text: '거래내역 권한부여',
              key: 'grantBankAccount',
              align: 'center',
              width: '80px',
              sortable: true,
              input: {
                type: 'button',
                trueColor: '#6f6f6f',
                color: '#01579B',
              },
              read: (item) => '권한부여'
            },
            {
              text: '업무지원 로그인 기한',
              key: 'csExpiredDatetime',
              width: '100px',
              read: (item) => `${item.csExpiredDatetime ? item.csExpiredDatetime.toLocaleString() : '-'}`
            },
          ]
        },
        {
          text: '대표자',
          child: [
            {
              text: '성명',
              key: 'ceoFullname',
              width: '80px',
              align: 'left',
            },
            {
              text: '전화번호',
              key: 'ceoMobileno',
              align: 'left',
              width: '100px',
              read: (item) => (item.ceoTelno ? 'T: ' + item.ceoTelno + '<br />' : '')
                + (item.ceoMobileno ? 'M: ' + item.ceoMobileno : ''),
            },
            {
              text: '이메일',
              key: 'ceoEmail',
              width: '200px',
              align: 'left',
            },
          ]
        },
        {
          text: '담당자',
          child: [
            {
              text: '성명',
              key: 'userFullname',
              width: '80px',
              align: 'left',
            },
            {
              text: '전화번호',
              key: 'userMobileno',
              width: '100px',
              read: (item) => (item.userTelno ? 'T: ' + item.userTelno + '<br />' : '')
                + (item.userMobileno ? 'M: ' + item.userMobileno : ''),
            },
            {
              text: '이메일',
              key: 'userEmail',
              width: '200px',
              align: 'left',
            },
          ]
        },
        {
          text: '주소',
          key: 'fullAddress',
          read: (item) => (item.address || '') + '<br />' + (item.addressDetail || ''),
          width: '300px',
          align: 'left',
        },/*
        {
          text: '모니터링',
          child: [
            {
              text: 'device',
              key: 'collectedDV',
              width: '40px',
              align: 'right',
            },
            {
              text: 'agent',
              key: 'collectedPC',
              width: '40px',
              align: 'right',
            },
            {
              text: 'printer',
              key: 'monitorngPrinterCnt',
              width: '40px',
              align: 'right',
            },
          ],
        },*/
        {
          text: 'CMS',
          width: '40px',
          read: (item) => item.fmsYn === 'Y' ? '효성' : item.niceYn === 'Y' ? '더빌' : '-',
        },
        {
          text: '국가',
          key: 'countryCodeName',
          width: '80px',
        },
        {
          text: '영업담당자 메모',
          value: 'memo',
          width: '100px',
        },
        {
          text: '모니터링주기설정',
          key: 'agentInterval',
          width: '100px',
        },
        {
          text: '거래처 마이그레이션',
          key: 'fileCount',
          width: '70px',
        },
        {
          text: '매출 확인',
          key: 'sales',
          width: '70px',
          input: {type: 'button', color: '#01579B'},
          read: (item) => '매출페이지',
          callBack: (item) => {
            this.$router.push({
              name: 'managertnbox.sales',
              query: {rentalcompanyId: item.rentalcompanyId},
            })
          },
        },
        {
          text: '임대업체 추가정보',
          child: [
            {
              text: '무상 기간 종료일',
              key: 'freeUseEndDate',
              width: '90px',
              sortable: true,
              input: {
                type: 'datepicker',
                callBack: (item) => this.onUpdateRentalCompanyInfo(item),
              },
            },
            {
              text: '최초 계약일',
              key: 'initContractDate',
              width: '90px',
              sortable: true,
              input: {
                type: 'datepicker',
                callBack: (item) => this.onUpdateRentalCompanyInfo(item),
              },
            },
            {
              text: '계약 시작일',
              key: 'curStartContractDate',
              width: '90px',
              sortable: true,
              input: {
                type: 'datepicker',
                callBack: (item) => this.onUpdateRentalCompanyInfo(item),
              },
            },
            {
              text: '청구 종료일',
              key: 'curEndContractDate',
              width: '90px',
              sortable: true,
              input: {
                type: 'datepicker',
                callBack: (item) => this.onUpdateRentalCompanyInfo(item),
              },
            },
            {
              text: '당 계약 종료일',
              key: 'toBeRenewContractDate',
              width: '90px',
              sortable: true,
              input: {
                type: 'datepicker',
                callBack: (item) => this.onUpdateRentalCompanyInfo(item),
              },
              class: (item, v) => this.conractEndCheckClass(v)
            },
            {
              text: '계약 금액',
              key: 'contractAmount',
              width: '100px',
              sortable: true,
              align: 'right',
              input: {
                type: 'number',
                callBack: (item) => this.onUpdateRentalCompanyInfo(item),
              },
            },
            {
              text: '최근 로그인',
              key: 'lastLoginDatetime',
              width: '100px',
              input: {type: 'button', color: '#01579B',
                callBack: (item) => this.onLoadRecentLogin(item),
              },
              read: (item) => {
                if (!!item.lastLoginDatetime)
                  return DateTime.fromISO(item.lastLoginDatetime).toFormat('yyyy-MM-dd HH:mm')
                return '조회하기'
              },
            },
            {
              text: '실 담당자 명',
              key: 'connectorName',
              width: '100px',
              sortable: true,
              align: 'right',
              input: {
                type: 'text',
                callBack: (item) => this.onUpdateRentalCompanyInfo(item),
                placeholder: '미입력'
              },
            },
            {
              text: '실 담당자 연락처',
              key: 'connectorMobile',
              width: '100px',
              sortable: true,
              align: 'right',
              input: {
                type: 'text',
                callBack: (item) => this.onUpdateRentalCompanyInfo(item),
              },
            },
            {
              text: '비고',
              key: 'remark01',
              width: '200px',
              sortable: true,
              align: 'left',
              input: {
                type: 'textarea',
                callBack: (item) => this.onUpdateRentalCompanyInfo(item),
              },
            },
          ],
        },
      ],
      // 임대업체 그룹 헤더
      rentalCompanyGroupHeaders: [
        {
          text: '임대업체 그룹명',
          key: 'groupName',
          width: '200px',
          align: 'left',
          sortable: true,
        },
        {
          text: '메모',
          key: 'memo',
          width: '400px',
          align: 'left',
          sortable: false,
        },
        {
          text: '삭제여부',
          key: 'deleteYn',
          width: '60px',
          align: 'center',
          sortable: true,
        },
        {
          text: '연결된 업체 수',
          key: 'usingRentalCompanies',
          width: '80px',
          align: 'center',
          sortable: true,
          read: (item) => item.usingRentalCompanies ? item.usingRentalCompanies.length : 0
        },
        {
          text: '등록일',
          key: 'regDatetime',
          width: '100px',
          align: 'center',
          sortable: true,
          read: (item) => {
            if (!!item.regDatetime)
              return DateTime.fromISO(item.regDatetime + '+00:00').toFormat('yyyy-MM-dd HH:mm')
          },
        },
        {
          text: '등록자',
          key: 'registerName',
          width: '100px',
          align: 'center',
          sortable: true,
        },
        {
          text: '수정일',
          key: 'updDatetime',
          width: '100px',
          align: 'center',
          sortable: true,
          read: (item) => {
            if (!!item.updDatetime)
              return DateTime.fromISO(item.updDatetime + '+00:00').toFormat('yyyy-MM-dd HH:mm')
          },
        },
        {
          text: '수정자',
          key: 'updaterName',
          width: '100px',
          align: 'center',
          sortable: true,
        },
      ],

      membershipStatsHeaders: [
        {
          pk: true,
          text: '업체코드',
          key: 'rentalcompanyId',
          width: '70px',
          sortable: true,
        },
        {
          text: '업체명',
          key: 'rentalcompanyName',
          width: '150px',
          sortable: true,
        },
        {
          text: '거래처',
          child: [
            {
              text: '합계',
              key: 'accTotalCnt',
              width: '40px',
              sortable: true,
              align: 'right',
            },
            {
              text: '계약중',
              key: 'accYCnt',
              width: '40px',
              sortable: true,
              align: 'right',
            },
            {
              text: '일시정지',
              key: 'accNCnt',
              width: '40px',
              sortable: true,
              align: 'right',
            },
            {
              text: '해지',
              key: 'accCCnt',
              width: '40px',
              sortable: true,
              align: 'right',
            },
          ]
        },
        {
          text: '모니터링',
          child: [
            {
              text: 'device',
              key: 'collectedDV',
              width: '40px',
              sortable: true,
              align: 'right',
            },
            {
              text: 'agent',
              key: 'collectedPC',
              width: '40px',
              sortable: true,
              align: 'right',
            },
            {
              text: 'printer',
              key: 'monitorngPrinterCnt',
              width: '40px',
              sortable: true,
              align: 'right',
            },
          ],
        },
        {
          text: 'CMS',
          child: [
            {
              text: '합계',
              key: 'cmsTotalCnt',
              width: '40px',
              sortable: true,
              align: 'right'
            },
            {
              text: '성공',
              key: 'cmsYCnt',
              width: '40px',
              sortable: true,
              align: 'right'
            },
            {
              text: '실패',
              key: 'cmsNCnt',
              width: '40px',
              sortable: true,
              align: 'right'
            },
            {
              text: '등록',
              key: 'cmsRCnt',
              width: '40px',
              sortable: true,
              align: 'right'
            },
            {
              text: '취소',
              key: 'cmsCCnt',
              width: '40px',
              sortable: true,
              align: 'right'
            },
          ]
        },
        {
          text: '세금계산서',
          child: [
            {
              text: '전체',
              key: 'taxTotalCnt',
              width: '80px',
              sortable: true,
              align: 'right',
              read: (item) => `${item.taxTotalCnt || '-'}<br />${item.taxTotalCnt * 132} 원`
            },
            {
              text: '성공',
              key: 'taxYCnt',
              width: '40px',
              sortable: true,
              align: 'right'
            },
            {
              text: '실패',
              key: 'taxNCnt',
              width: '40px',
              sortable: true,
              align: 'right'
            },
            {
              text: '등록',
              key: 'taxPCnt',
              width: '40px',
              sortable: true,
              align: 'right'
            },
            {
              text: '에러',
              key: 'taxECnt',
              width: '40px',
              sortable: true,
              align: 'right'
            },
          ]
        },
        {
          text: '거래명세서',
          child: [
            {
              text: '전체',
              key: 'transTotalCnt',
              width: '40px',
              sortable: true,
              align: 'right'
            },
            {
              text: '성공',
              key: 'transYCnt',
              width: '40px',
              sortable: true,
              align: 'right'
            },
            {
              text: '실패',
              key: 'transNCnt',
              width: '40px',
              sortable: true,
              align: 'right'
            },
          ]
        },
      ],
      agencyManagerHeader: [
        {
          text: '담당자 명',
          key: 'name',
          width: '60px',
          align: 'left',
          sortable: false,
          input: {
            type: 'text',
            callBack: (item) => this.onModifyManager(item, 'name'),
          },
        },
        {
          text: '담당자 번호',
          key: 'changeAmUserId',
          width: '30px',
          align: 'center',
          sortable: false,
          input: {
            type: 'text',
            callBack: (item) => this.onModifyManager(item, 'amUserId'),
          },
        },
        {
          text: '사용여부',
          key: 'useYn',
          width: '60px',
          align: 'center',
          sortable: false,
          input: {
            type: 'button',
            trueValue: 'Y',
            trueColor: '#01579B',
            falseValue: 'N',
            color: '#263238',
            callBack: (item) => this.onModifyManager(item, 'useYn'),
          },
        },
        {
          text: '관리 업체 수',
          key: 'companyCount',
          width: '80px',
          align: 'center',
          sortable: true,
        },
      ]
    }
  },
}
</script>
